import React from 'react';
import styled from 'styled-components';
import { format } from 'date-fns';
import ReactMarkdown from 'react-markdown';

import Zoom from 'react-medium-image-zoom';
import 'react-medium-image-zoom/dist/styles.css';

import dynamic from 'next/dynamic';
import Image from './Image';

import { BlogPostContentFragment } from '../schemas/api';

import SubscribeSection from './SubscribeSection';

import BREAKPOINTS from '../styles/breakpoints';
import { Grid, columns } from '../styles/grid';
import COLORS from '../styles/colors';
import TYPO from '../styles/typography';
import MarkdownStyle from '../styles/markdown';

import ShareSection from './ShareSection';

const BlogPostContent = dynamic(() => import('./BlogPostContent'), {
  ssr: false,
});

const TEXT_MAX_WIDTH = 640;

const Container = styled(Grid)`
  padding: 0 0 60px;
  ${BREAKPOINTS.max.medium`
    padding: 72px 0 36px;
  `}
`;

const Header = styled.div`
  ${columns(1, 12)};
  max-width: ${TEXT_MAX_WIDTH}px;
  margin: 60px auto 12px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 0 12px;

  ${BREAKPOINTS.max.small`
  padding: 0 24px;
`}
`;

const PreviewText = styled.div`
  ${columns(1, 12)};
  padding: 24px 12px 12px;
  max-width: ${TEXT_MAX_WIDTH}px;
  margin: 0 auto;
  ${MarkdownStyle}

  ${TYPO.p1};
  font-size: 18px;

  ${BREAKPOINTS.max.small`
  padding: 24px 24px 12px;
`}
`;

const PreviewImage = styled.div`
  ${columns(1, 12)};
  padding: 48px 0;
  & > div {
    margin: 0 auto;
    overflow: hidden;
    border-radius: 6px;
  }
  & img {
    width: 100%;
    height: auto;
    margin: 0 auto;
  }

  display: flex;
  justify-content: center;
  ${BREAKPOINTS.max.small`
    & > div {
    border-radius: 0px;
  }
  `}
`;

const Title = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const Heading = styled.div`
  ${TYPO.h1};
`;

const Content = styled.div`
  ${columns(1, 12)};
  padding: 0 12px;
  font-size: 16px;
  ${MarkdownStyle}

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    max-width: ${TEXT_MAX_WIDTH}px;
    margin: 60px auto 12px;
    padding-bottom: 12px;
  }

  p,
  blockquote,
  ul,
  ol,
  dl,
  table,
  pre {
    max-width: ${TEXT_MAX_WIDTH}px;
    margin: 20px auto 0;
    line-height: 180%;
  }

  blockquote > p,
  pre > p {
    margin: 0;
  }

  ${BREAKPOINTS.max.small`
    font-size: 15px;
    padding: 0 24px;
    & > figure {
      margin: 24px auto;
    }
  `}
`;

const DateContainer = styled.div`
  color: ${COLORS.shades.s300.css};
  ${TYPO.subheading}
`;

const Author = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  ${TYPO.p2};
  font-weight: 600;
`;

type PictureProps = { $src: string };

const Picture = styled.div<PictureProps>`
  background-image: ${(props) => `url('${props.$src}')`};
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-size: cover;
`;

const Footer = styled.div`
  ${columns(1, 12)};
  padding: 160px 12px 0;
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 72px;
`;

type BlogPostProps = {
  data: BlogPostContentFragment;
};

export default function BlogPost({ data }: BlogPostProps) {
  return (
    <Container>
      <Header>
        <Title>
          <DateContainer>
            {format(
              new Date(data.publicationDate || data.sys.publishedAt),
              'MMM dd, yyyy'
            )}
          </DateContainer>
          <Heading>{data.title}</Heading>
        </Title>
        {data.author && (
          <Author>
            <Picture $src={data.author.picture.url} />
            <span>{data.author.name}</span>
          </Author>
        )}
      </Header>
      {data.short && (
        <PreviewText>
          <ReactMarkdown linkTarget="_blank">{data.short}</ReactMarkdown>
        </PreviewText>
      )}
      {data.image && (
        <PreviewImage>
          <Zoom zoomMargin={36} wrapStyle={{ width: '100%' }}>
            <Image
              src={data.image.url}
              width={data.image.width}
              height={data.image.height}
              alt={data.title}
              style={{ width: '100%', height: 'auto' }}
              priority
            />
          </Zoom>
        </PreviewImage>
      )}
      <Content>
        <BlogPostContent>{data.content}</BlogPostContent>
        {data.contentTwo && (
          <BlogPostContent>{data.contentTwo}</BlogPostContent>
        )}
      </Content>
      <Footer>
        <SubscribeSection />
        <ShareSection />
      </Footer>
    </Container>
  );
}
